import { tokens } from '@ui/designTokens';
import { SoundEngine } from '@audio/soundEngine';
import { ClaweyHud } from '@ui/components/claweyHud';
import { GeometryStorage } from '@storage/geometryStorage';
import { StationProgression } from '@storage/stationProgression';

export interface GridPoint {
  x: number;
  y: number;
}

export interface ReflectionPuzzle {
  id: string;
  title: string;
  mirrorAxis: 'vertical' | 'horizontal';
  mirrorLineCoord: number; // e.g. x = 5 or y = 5
  originalPoints: GridPoint[];
  shapeLabel: string;
  hint: string;
}

export interface ReflectionPuzzleSet {
  setNumber: number;
  title: string;
  theme: string;
  puzzles: ReflectionPuzzle[];
}

export const REFLECTION_PUZZLE_SETS: ReflectionPuzzleSet[] = [
  {
    setNumber: 1,
    title: 'Shallows Specimens',
    theme: 'Vertical Line x = 5 (Single Points)',
    puzzles: [
      {
        id: 'ref-1',
        title: 'Starfish Reflection (Vertical Mirror Line x = 5)',
        mirrorAxis: 'vertical',
        mirrorLineCoord: 5,
        originalPoints: [{ x: 2, y: 7 }],
        shapeLabel: 'Golden Starfish',
        hint: 'The Starfish is at (2, 7), which is 3 squares left of the mirror line at x=5. Reflect it 3 squares to the right: (8, 7)!',
      },
      {
        id: 'ref-2',
        title: 'Marine Pearl Reflection (Vertical Mirror Line x = 5)',
        mirrorAxis: 'vertical',
        mirrorLineCoord: 5,
        originalPoints: [{ x: 1, y: 4 }],
        shapeLabel: 'Deep Sea Pearl',
        hint: 'Point (1, 4) is 4 squares away from the mirror line. Where is 4 squares to the right?',
      },
    ],
  },
  {
    setNumber: 2,
    title: 'Tidal Pool Horizons',
    theme: 'Horizontal Line y = 5 (Single Points)',
    puzzles: [
      {
        id: 'ref-4',
        title: 'Tide Pool Crab (Horizontal Mirror Line y = 5)',
        mirrorAxis: 'horizontal',
        mirrorLineCoord: 5,
        originalPoints: [{ x: 6, y: 8 }],
        shapeLabel: 'Shore Crab',
        hint: 'The Crab is at (6, 8), which is 3 units above y=5. Its reflection is 3 units below y=5: at (6, 2)!',
      },
      {
        id: 'ref-2-2',
        title: 'Amber Sea Snail (Horizontal Line y = 5)',
        mirrorAxis: 'horizontal',
        mirrorLineCoord: 5,
        originalPoints: [{ x: 3, y: 9 }],
        shapeLabel: 'Amber Sea Snail',
        hint: 'The Snail is at (3, 9), 4 units above y=5. Reflect it 4 units below y=5: at (3, 1)!',
      },
    ],
  },
  {
    setNumber: 3,
    title: 'Marine Line Segments',
    theme: 'Vertical Line x = 5 (2-Point Segments)',
    puzzles: [
      {
        id: 'ref-3-1',
        title: 'Anchor Bar (Vertical Line x = 5)',
        mirrorAxis: 'vertical',
        mirrorLineCoord: 5,
        originalPoints: [{ x: 2, y: 3 }, { x: 2, y: 7 }],
        shapeLabel: 'Iron Anchor Bar',
        hint: 'Both endpoints are at x=2 (3 units left of x=5). Reflect both points to x=8!',
      },
      {
        id: 'ref-3-2',
        title: 'Mast Rigging (Vertical Line x = 5)',
        mirrorAxis: 'vertical',
        mirrorLineCoord: 5,
        originalPoints: [{ x: 4, y: 2 }, { x: 4, y: 8 }],
        shapeLabel: 'Ship Mast Rigging',
        hint: 'Points at x=4 are 1 square left of x=5. Their reflection is 1 square right: at x=6!',
      },
    ],
  },
  {
    setNumber: 4,
    title: 'Horizontal Segments',
    theme: 'Horizontal Line y = 5 (2-Point Segments)',
    puzzles: [
      {
        id: 'ref-4-1',
        title: 'Kelp Stalk (Horizontal Line y = 5)',
        mirrorAxis: 'horizontal',
        mirrorLineCoord: 5,
        originalPoints: [{ x: 3, y: 7 }, { x: 7, y: 7 }],
        shapeLabel: 'Kelp Stalk',
        hint: 'Points at y=7 are 2 squares above y=5. Reflect them 2 squares below: to y=3!',
      },
      {
        id: 'ref-4-2',
        title: 'Sponge Ledge (Horizontal Line y = 5)',
        mirrorAxis: 'horizontal',
        mirrorLineCoord: 5,
        originalPoints: [{ x: 2, y: 6 }, { x: 8, y: 6 }],
        shapeLabel: 'Sponge Coral Ledge',
        hint: 'Points at y=6 are 1 unit above y=5. Reflect them 1 unit below: to y=4!',
      },
    ],
  },
  {
    setNumber: 5,
    title: 'Coral Triangles',
    theme: 'Vertical Line x = 5 (3 Vertices)',
    puzzles: [
      {
        id: 'ref-3',
        title: 'Coral Triangle (Vertical Mirror Line x = 5)',
        mirrorAxis: 'vertical',
        mirrorLineCoord: 5,
        originalPoints: [
          { x: 3, y: 2 },
          { x: 1, y: 2 },
          { x: 3, y: 6 },
        ],
        shapeLabel: 'Right-Angled Coral Triangle',
        hint: 'Reflect each vertex across x=5: x=3 reflects to x=7, and x=1 reflects to x=9!',
      },
      {
        id: 'ref-5-2',
        title: 'Sailfin Shark Fin (Vertical Line x = 5)',
        mirrorAxis: 'vertical',
        mirrorLineCoord: 5,
        originalPoints: [
          { x: 4, y: 3 },
          { x: 2, y: 3 },
          { x: 4, y: 7 },
        ],
        shapeLabel: 'Sailfin Dorsal Fin',
        hint: 'Points at x=4 reflect to x=6, and point at x=2 reflects to x=8!',
      },
    ],
  },
  {
    setNumber: 6,
    title: 'Horizontal Triangles',
    theme: 'Horizontal Line y = 5 (3 Vertices)',
    puzzles: [
      {
        id: 'ref-6-1',
        title: 'Manta Ray Wing (Horizontal Line y = 5)',
        mirrorAxis: 'horizontal',
        mirrorLineCoord: 5,
        originalPoints: [
          { x: 5, y: 8 },
          { x: 3, y: 6 },
          { x: 7, y: 6 },
        ],
        shapeLabel: 'Manta Ray Wing',
        hint: 'Peak at (5, 8) reflects to (5, 2); base corners at y=6 reflect to y=4!',
      },
    ],
  },
  {
    setNumber: 7,
    title: 'Offset Vertical Mirrors',
    theme: 'Vertical Mirror Line x = 4',
    puzzles: [
      {
        id: 'ref-7-1',
        title: 'Sea Urchin (Mirror Line x = 4)',
        mirrorAxis: 'vertical',
        mirrorLineCoord: 4,
        originalPoints: [{ x: 2, y: 4 }],
        shapeLabel: 'Spiny Sea Urchin',
        hint: 'The urchin at x=2 is 2 units left of the mirror line at x=4. Reflect 2 units right: to (6, 4)!',
      },
      {
        id: 'ref-7-2',
        title: 'Anemone Column (Mirror Line x = 4)',
        mirrorAxis: 'vertical',
        mirrorLineCoord: 4,
        originalPoints: [{ x: 1, y: 6 }, { x: 1, y: 2 }],
        shapeLabel: 'Anemone Column',
        hint: 'Points at x=1 are 3 units left of x=4. Reflect them 3 units right: to x=7!',
      },
    ],
  },
  {
    setNumber: 8,
    title: 'Offset Horizontal Mirrors',
    theme: 'Horizontal Mirror Line y = 6',
    puzzles: [
      {
        id: 'ref-8-1',
        title: 'Nautilus Shell (Mirror Line y = 6)',
        mirrorAxis: 'horizontal',
        mirrorLineCoord: 6,
        originalPoints: [{ x: 4, y: 8 }],
        shapeLabel: 'Spiraled Nautilus Shell',
        hint: 'Point at y=8 is 2 units above the mirror line at y=6. Reflect it 2 units below: to (4, 4)!',
      },
    ],
  },
  {
    setNumber: 9,
    title: 'Naturalist Quadrilaterals',
    theme: '4-Point Marine Polygons',
    puzzles: [
      {
        id: 'ref-9-1',
        title: 'Diamond Ray (Vertical Line x = 5)',
        mirrorAxis: 'vertical',
        mirrorLineCoord: 5,
        originalPoints: [
          { x: 3, y: 5 },
          { x: 4, y: 7 },
          { x: 4, y: 3 },
          { x: 2, y: 5 },
        ],
        shapeLabel: 'Deep Sea Diamond Ray',
        hint: 'Reflect each diamond vertex across x=5: x=3->x=7, x=4->x=6, x=2->x=8!',
      },
    ],
  },
  {
    setNumber: 10,
    title: 'Master Naturalist Crest',
    theme: 'H.M.S. Challenger Symmetry',
    puzzles: [
      {
        id: 'ref-10-1',
        title: 'Cartographer Compass Rose (Line x = 5)',
        mirrorAxis: 'vertical',
        mirrorLineCoord: 5,
        originalPoints: [
          { x: 4, y: 6 },
          { x: 3, y: 5 },
          { x: 4, y: 4 },
        ],
        shapeLabel: 'Compass Rose Pointer',
        hint: 'Reflect vertices across x=5: x=4 reflects to x=6, x=3 reflects to x=7!',
      },
    ],
  },
];

export const REFLECTION_PUZZLES: ReflectionPuzzle[] = REFLECTION_PUZZLE_SETS[0]!.puzzles;

export class BenthicMirrorLine {
  private container: HTMLElement;
  private sound: SoundEngine;
  private claweyHud?: ClaweyHud;
  private currentSetNumber: number = 1;
  private currentPuzzleIndex: number = 0;
  private setScore: number = 0;
  private placedPoints: GridPoint[] = [];
  private streak: number = 0;

  private get currentSet(): ReflectionPuzzleSet {
    return REFLECTION_PUZZLE_SETS[this.currentSetNumber - 1] ?? REFLECTION_PUZZLE_SETS[0]!;
  }

  constructor(sound?: SoundEngine, claweyHud?: ClaweyHud) {
    this.container = document.createElement('div');
    this.container.className = 'benthic-mirror-station';
    this.sound = sound || new SoundEngine();
    this.claweyHud = claweyHud;

    const prog = StationProgression.getProgression('geometry-mirror');
    this.currentSetNumber = prog.currentSet;
    this.resetPlacedPoints();
  }

  private resetPlacedPoints(): void {
    const currentSet = this.currentSet;
    const puzzle = currentSet.puzzles[this.currentPuzzleIndex] ?? currentSet.puzzles[0];
    if (puzzle) {
      // Start with initial placement at center of opposite side
      this.placedPoints = puzzle.originalPoints.map((pt) => ({
        x: puzzle.mirrorAxis === 'vertical' ? 2 * puzzle.mirrorLineCoord - pt.x : pt.x,
        y: puzzle.mirrorAxis === 'horizontal' ? 2 * puzzle.mirrorLineCoord - pt.y : pt.y,
      }));
    }
  }

  render(): HTMLElement {
    this.updateView();
    return this.container;
  }

  private updateView(): void {
    const currentSet = this.currentSet;
    const puzzle = currentSet.puzzles[this.currentPuzzleIndex] ?? currentSet.puzzles[0];
    if (!puzzle) return;

    this.container.innerHTML = `
      <div style="background: rgba(255, 253, 249, 0.95); border: 1.5px solid ${tokens.color.gildedBrass}; border-radius: 16px; padding: 24px; box-shadow: 0 6px 24px rgba(7, 20, 36, 0.08);">
        <!-- 10-Tier Set Navigation Carousel (FR-PROG-001) -->
        <div style="margin-bottom: 18px;">
          <div style="font-size: 0.8rem; font-weight: 800; color: ${tokens.color.seaweedSage}; text-transform: uppercase; margin-bottom: 8px; display: flex; justify-content: space-between; align-items: center;">
            <span>🪞 Reflective Symmetry · 10 Progressive Challenge Sets</span>
            <span style="color: ${tokens.color.morrisIndigo};">
              Mastered: ${StationProgression.getCompletedSetCount('geometry-mirror')} of 10 Sets
            </span>
          </div>
          <div style="display: flex; gap: 8px; overflow-x: auto; padding: 4px 2px 10px; scrollbar-width: thin;">
            ${REFLECTION_PUZZLE_SETS.map((s) => {
              const isUnlocked = StationProgression.isSetUnlocked('geometry-mirror', s.setNumber);
              const isCurrent = this.currentSetNumber === s.setNumber;
              const prog = StationProgression.getProgression('geometry-mirror');
              const comp = prog.completedSets[s.setNumber];
              const starsStr = comp ? ' ' + '★'.repeat(comp.stars) : '';

              return `
                <button
                  class="touch-target mirror-set-pill-btn"
                  data-set-num="${s.setNumber}"
                  ${!isUnlocked ? 'disabled style="opacity: 0.45; cursor: not-allowed;"' : ''}
                  style="
                    padding: 8px 14px;
                    font-size: 0.85rem;
                    font-weight: 800;
                    border-radius: 20px;
                    white-space: nowrap;
                    border: 1.5px solid ${isCurrent ? tokens.color.gildedBrass : 'rgba(26,54,93,0.2)'};
                    background: ${isCurrent ? 'rgba(255, 246, 229, 0.95)' : '#FFFDF9'};
                    color: ${tokens.color.morrisIndigo};
                    cursor: ${isUnlocked ? 'pointer' : 'not-allowed'};
                  "
                >
                  ${!isUnlocked ? '🔒 ' : ''}Set ${s.setNumber}: ${s.title}${starsStr}
                </button>
              `;
            }).join('')}
          </div>
        </div>

        <!-- Header -->
        <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 14px; margin-bottom: 16px;">
          <div>
            <div style="font-size: 0.85rem; font-weight: 800; color: ${tokens.color.seaweedSage}; text-transform: uppercase;">
              Puzzle ${this.currentPuzzleIndex + 1} of ${currentSet.puzzles.length} · Set ${this.currentSetNumber}: ${currentSet.title}
            </div>
            <h2 style="font-size: 1.4rem; color: ${tokens.color.morrisIndigo}; margin: 4px 0 0;">
              ${puzzle.title}
            </h2>
          </div>

          <div style="display: flex; align-items: center; gap: 12px;">
            <span style="font-weight: 800; color: ${tokens.color.madderCoral}; font-size: 0.95rem;">
              🔥 Streak: ${this.streak}
            </span>
            <button id="btn-mirror-hint" class="touch-target" style="padding: 8px 16px; font-size: 0.9rem; border: 1.5px dashed ${tokens.color.gildedBrass}; border-radius: 20px; background: #FFFDF9; color: ${tokens.color.morrisIndigo}; font-weight: 700;">
              💡 Mirror Clue
            </button>
          </div>
        </div>

        <p style="font-size: 1rem; color: #3A4D66; margin-bottom: 16px; line-height: 1.5;">
          Tap on the grid to position the reflected <b>${puzzle.shapeLabel}</b> points across the golden dashed mirror line.
        </p>

        <!-- SVG Grid Container -->
        <div style="display: flex; justify-content: center; align-items: center; width: 100%; margin: 12px 0;">
          ${this.generateGridSvg(puzzle)}
        </div>

        <!-- Controls & Check Button -->
        <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 14px; margin-top: 18px;">
          <div style="display: flex; gap: 12px; align-items: center;">
            <span style="font-size: 0.9rem; font-weight: 700; color: ${tokens.color.morrisIndigo};">
              Placed Coordinates:
            </span>
            ${this.placedPoints.map((p) => `<span style="padding: 4px 10px; background: rgba(255, 107, 74, 0.15); border-radius: 8px; font-weight: 800; font-family: monospace;">(${p.x}, ${p.y})</span>`).join(' ')}
          </div>

          <div style="display: flex; gap: 10px;">
            <button id="btn-check-reflection" class="touch-target btn-primary" style="padding: 12px 28px; font-size: 1.05rem;">
              Check Reflection &rarr;
            </button>
          </div>
        </div>
      </div>
    `;

    this.attachListeners(puzzle);
  }

  private generateGridSvg(puzzle: ReflectionPuzzle): string {
    const size = 360;
    const padding = 35;
    const gridSize = 10;
    const step = (size - padding * 2) / gridSize;

    // Grid lines
    const lines: string[] = [];
    for (let i = 0; i <= gridSize; i++) {
      const pos = padding + i * step;

      // Vertical line
      lines.push(`
        <line x1="${pos}" y1="${padding}" x2="${pos}" y2="${size - padding}" stroke="#E2E7ED" stroke-width="1.5" />
        <text x="${pos}" y="${size - padding + 18}" font-size="11" font-weight="700" fill="#7A8C9E" text-anchor="middle">${i}</text>
      `);

      // Horizontal line
      lines.push(`
        <line x1="${padding}" y1="${pos}" x2="${size - padding}" y2="${pos}" stroke="#E2E7ED" stroke-width="1.5" />
        <text x="${padding - 14}" y="${size - pos + 4}" font-size="11" font-weight="700" fill="#7A8C9E" text-anchor="middle">${i}</text>
      `);
    }

    // Mirror Line
    let mirrorSvg = '';
    if (puzzle.mirrorAxis === 'vertical') {
      const mx = padding + puzzle.mirrorLineCoord * step;
      mirrorSvg = `
        <line x1="${mx}" y1="${padding - 10}" x2="${mx}" y2="${size - padding + 10}" stroke="${tokens.color.gildedBrass}" stroke-width="3.5" stroke-dasharray="6" />
        <text x="${mx}" y="${padding - 16}" font-size="12" font-weight="800" fill="${tokens.color.gildedBrass}" text-anchor="middle">MIRROR LINE (x = ${puzzle.mirrorLineCoord})</text>
      `;
    } else {
      const my = size - (padding + puzzle.mirrorLineCoord * step);
      mirrorSvg = `
        <line x1="${padding - 10}" y1="${my}" x2="${size - padding + 10}" y2="${my}" stroke="${tokens.color.gildedBrass}" stroke-width="3.5" stroke-dasharray="6" />
        <text x="${size - padding + 12}" y="${my + 4}" font-size="12" font-weight="800" fill="${tokens.color.gildedBrass}" text-anchor="start">y = ${puzzle.mirrorLineCoord}</text>
      `;
    }

    // Original shape SVG
    const originalPointsSvg = puzzle.originalPoints
      .map((pt) => {
        const cx = padding + pt.x * step;
        const cy = size - (padding + pt.y * step);
        return `
        <circle cx="${cx}" cy="${cy}" r="9" fill="${tokens.color.morrisIndigo}" stroke="#FFFDF9" stroke-width="2" />
        <text x="${cx}" y="${cy - 14}" font-size="11" font-weight="800" fill="${tokens.color.morrisIndigo}" text-anchor="middle">(${pt.x},${pt.y})</text>
      `;
      })
      .join('');

    // Connecting polygon if > 1 point
    let originalPoly = '';
    if (puzzle.originalPoints.length > 1) {
      const pts = puzzle.originalPoints
        .map((pt) => `${padding + pt.x * step},${size - (padding + pt.y * step)}`)
        .join(' ');
      originalPoly = `<polygon points="${pts}" fill="rgba(7, 20, 36, 0.12)" stroke="${tokens.color.morrisIndigo}" stroke-width="2.5" />`;
    }

    // Placed user points SVG
    const placedPointsSvg = this.placedPoints
      .map((pt, idx) => {
        const cx = padding + pt.x * step;
        const cy = size - (padding + pt.y * step);
        return `
        <g class="placed-point-marker" data-index="${idx}" style="cursor: pointer;">
          <circle cx="${cx}" cy="${cy}" r="14" fill="${tokens.color.madderCoral}" stroke="#FFFDF9" stroke-width="2.5" filter="drop-shadow(0 2px 6px rgba(255, 107, 74, 0.4))" />
          <circle cx="${cx}" cy="${cy}" r="4" fill="#FFFDF9" />
          <text x="${cx}" y="${cy - 18}" font-size="11" font-weight="800" fill="${tokens.color.madderCoral}" text-anchor="middle">(${pt.x},${pt.y})</text>
        </g>
      `;
      })
      .join('');

    let placedPoly = '';
    if (this.placedPoints.length > 1) {
      const pts = this.placedPoints
        .map((pt) => `${padding + pt.x * step},${size - (padding + pt.y * step)}`)
        .join(' ');
      placedPoly = `<polygon points="${pts}" fill="rgba(255, 107, 74, 0.15)" stroke="${tokens.color.madderCoral}" stroke-width="2" stroke-dasharray="4" />`;
    }

    return `
      <svg id="mirror-grid-svg" viewBox="0 0 ${size} ${size}" style="width: 100%; max-width: 420px; height: auto; user-select: none; background: #FFFDF9; border: 1.5px solid rgba(229, 169, 60, 0.4); border-radius: 14px; box-shadow: 0 4px 16px rgba(7, 20, 36, 0.06);">
        ${lines.join('')}
        ${mirrorSvg}
        ${originalPoly}
        ${originalPointsSvg}
        ${placedPoly}
        ${placedPointsSvg}
      </svg>
    `;
  }

  private attachListeners(puzzle: ReflectionPuzzle): void {
    // Hint button
    const hintBtn = this.container.querySelector<HTMLButtonElement>('#btn-mirror-hint');
    hintBtn?.addEventListener('click', () => {
      this.sound.playTap();
      if (this.claweyHud) {
        this.claweyHud.say(puzzle.hint, 'thinking');
      }
    });

    // Grid tapping to place or adjust points
    const svg = this.container.querySelector<SVGSVGElement>('#mirror-grid-svg');
    if (svg) {
      svg.addEventListener('click', (e) => {
        const pt = svg.createSVGPoint();
        pt.x = e.clientX;
        pt.y = e.clientY;

        const ctm = svg.getScreenCTM();
        if (!ctm) return;
        const svgP = pt.matrixTransform(ctm.inverse());

        const size = 360;
        const padding = 35;
        const gridSize = 10;
        const step = (size - padding * 2) / gridSize;

        // Convert to grid coords 0-10
        const gx = Math.round((svgP.x - padding) / step);
        const gy = Math.round((size - padding - svgP.y) / step);

        if (gx >= 0 && gx <= 10 && gy >= 0 && gy <= 10) {
          this.sound.playTap();
          if (this.placedPoints.length > 0) {
            this.placedPoints[0] = { x: gx, y: gy };
          } else {
            this.placedPoints.push({ x: gx, y: gy });
          }
          this.updateView();
        }
      });
    }

    // Set pill buttons (10-Tier Navigation)
    const setPills = this.container.querySelectorAll<HTMLButtonElement>('.mirror-set-pill-btn');
    setPills.forEach((btn) => {
      btn.addEventListener('click', () => {
        const setNum = parseInt(btn.getAttribute('data-set-num') || '1', 10);
        this.sound.playTap();
        this.currentSetNumber = setNum;
        this.currentPuzzleIndex = 0;
        this.setScore = 0;
        this.resetPlacedPoints();
        this.updateView();
      });
    });

    // Check reflection button
    const checkBtn = this.container.querySelector<HTMLButtonElement>('#btn-check-reflection');
    checkBtn?.addEventListener('click', () => {
      this.validateReflection(puzzle);
    });
  }

  /** Compute mathematically correct reflection points */
  static calculateReflection(
    points: GridPoint[],
    axis: 'vertical' | 'horizontal',
    lineCoord: number
  ): GridPoint[] {
    return points.map((p) => {
      if (axis === 'vertical') {
        return {
          x: 2 * lineCoord - p.x,
          y: p.y,
        };
      } else {
        return {
          x: p.x,
          y: 2 * lineCoord - p.y,
        };
      }
    });
  }

  validateReflection(puzzle: ReflectionPuzzle): boolean {
    const expected = BenthicMirrorLine.calculateReflection(
      puzzle.originalPoints,
      puzzle.mirrorAxis,
      puzzle.mirrorLineCoord
    );

    if (this.placedPoints.length !== expected.length) return false;

    let allMatch = true;
    for (let i = 0; i < expected.length; i++) {
      const exp = expected[i];
      const actual = this.placedPoints[i];
      if (!exp || !actual || exp.x !== actual.x || exp.y !== actual.y) {
        allMatch = false;
        break;
      }
    }

    GeometryStorage.recordAttempt({
      tool: 'mirror',
      targetValue: `${expected[0]?.x},${expected[0]?.y}`,
      actualValue: `${this.placedPoints[0]?.x},${this.placedPoints[0]?.y}`,
      correct: allMatch,
      timestamp: Date.now(),
    });

    if (allMatch) {
      this.streak += 1;
      this.setScore += 1;
      this.sound.playSuccess();
      const currentSet = this.currentSet;
      const isLastInSet = this.currentPuzzleIndex >= currentSet.puzzles.length - 1;

      if (this.claweyHud) {
        if (isLastInSet) {
          this.claweyHud.say(
            `Sensational reflection, Tudor! You conquered Set ${this.currentSetNumber}: ${currentSet.title}! +25 Explorer Points!`,
            'celebrating'
          );
        } else {
          this.claweyHud.say(
            `Sensational reflection, Tudor! The distance to the mirror line matches perfectly on both sides!`,
            'celebrating'
          );
        }
      }

      if (isLastInSet) {
        StationProgression.completeSet(
          'geometry-mirror',
          this.currentSetNumber,
          this.setScore,
          currentSet.puzzles.length
        );
      }

      setTimeout(() => {
        if (isLastInSet) {
          if (this.currentSetNumber < 10) {
            this.currentSetNumber += 1;
            this.currentPuzzleIndex = 0;
            this.setScore = 0;
          } else {
            this.currentPuzzleIndex = 0;
          }
        } else {
          this.currentPuzzleIndex = (this.currentPuzzleIndex + 1) % currentSet.puzzles.length;
        }
        this.resetPlacedPoints();
        this.updateView();
      }, 1500);

      return true;
    } else {
      this.streak = 0;
      this.sound.playTryAgain();
      if (this.claweyHud) {
        this.claweyHud.say(puzzle.hint, 'thinking');
      }
      return false;
    }
  }

  getElement(): HTMLElement {
    return this.container;
  }
}
